import styled, { css } from 'styled-components';
import { rem } from '~/src/helpers/rem-converter.helper';
import { BORDER_RADIUS } from '~/src/styles/tokens/border-radius';
import { COLORS } from '~/src/styles/tokens/colors';
import { mediaQuery } from '~/src/styles/mixins';
import { MAX_767, MIN_768 } from '~/src/styles/tokens/break-points';
import { FONTS } from '~/src/styles/style-helpers/font-helper';
import { Z_INDEX } from '~/src/styles/tokens/z-index';
import { SvgSuccess } from '../../svg-components/success';
import type { mainTextAlignType } from '~/src/components/common-components/dropzone-uploader/dropzone-uploader.types';
import type { SuccessIconProps } from '../../svg-components/success';

const MOBILE_MINIMAL_LOCAL = 420;

export const DropZoneContainer = styled.div<{ isDragging: boolean; withIcon?: boolean }>`
    height: fit-content;
    padding: ${rem(12)};
    position: relative;
    border-radius: ${BORDER_RADIUS.normal8};
    border-width: 2px;
    border-style: dashed;
    border-color: ${COLORS.controlsInactive};
    gap: ${rem(16)};
    background: ${(p) => (p.isDragging ? `${COLORS.constantLightestBlueberry}` : 'none')};
    align-items: center;
    justify-content: center;
    flex-direction: column;

    ${(p) => p && css`
        ${mediaQuery({ minWidth: MIN_768 }, css`
            min-height: ${rem(p.withIcon ? 128 : 52)};
        `,
    )}`}

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            width: 100%;
            gap: ${rem(8)};
            padding: ${rem(12)};
        `,
    )};
`;

export const ErrorHiddenInput = styled.input`
    all: unset;
    position: absolute;
    height: 0;
    width: 0;
    pointer-events: none;
    opacity: 0;
`;

export const ImageWrapper = styled.div<{ isDragging: boolean }>`
    position: relative;
    width: ${rem(40)};
    min-width: ${rem(40)};
    height: ${rem(40)};
    justify-content: center;
    align-items: center;
    border-radius: ${BORDER_RADIUS.round};
    background-color: ${(p) => p.isDragging ? `${COLORS.backgroundPrimary}` : `${COLORS.backgroundSecondary}`};
  
    ${(p) => p && css`
        ${mediaQuery({ maxWidth: MAX_767 }, css`
            min-width: ${rem(32)};
            width: ${rem(32)};
            height: ${rem(32)};
        `,
    )}`}
`;

export const DropZoneWrapper = styled.div<{ needCentering: boolean; titleIsHide: boolean }>`
    position: relative;
    flex-direction: column;
    gap: ${(p) => (p.titleIsHide ? 0 : rem(8))};

    ${(p) =>
        p.needCentering &&
        css`
            justify-content: flex-start;
        `};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            width: 100%;
        `,
    )};
`;

export const DropZoneHideWrapper = styled.div`
    gap: ${rem(8)};
    flex-direction: column;

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            width: 100%;
        `,
    )};
`;

export const SuccessIcon = styled(SvgSuccess)<SuccessIconProps>`
    position: absolute;
    top: ${rem(-9)};
    right: ${rem(-8)};
`;

export const MainTextContainer = styled.div<{ mainTextAlign: mainTextAlignType }>`
    width: 100%;
    min-height: ${rem(24)};
    flex-direction: column;
    justify-content: center;
    align-items: ${(p) => (p.mainTextAlign === 'left' ? 'flex-start' : 'center')};
    gap: ${rem(6)};
    padding-left: ${(p) => (p.mainTextAlign === 'left' ? rem(56) : 'initial')};

    ${(p) => p && css`
        ${mediaQuery({ maxWidth: MAX_767 }, css`
            min-height: ${rem(20)};
            padding-left: ${p.mainTextAlign === 'left' ? rem(48) : 'initial'};
        `,
    )}`}
`;

export const IconContainer = styled.div`
    width: 100%;
    gap: ${rem(16)};
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
`;

export const MaxCountText = styled.div`
    ${FONTS.compact.regular.size14};
    color: ${COLORS.textSecondary};
    justify-content: center;

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            ${FONTS.compact.regular.size12};
        `,
    )};
`;

export const Title = styled.span`
    ${FONTS.compact.medium.size17};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            ${FONTS.compact.medium.size12};
        `,
    )};
`;

export const DraggableArea = styled.div<{ isEntered: boolean }>`
    min-height: ${rem(52)};
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: ${(p) => (p.isEntered ? `${Z_INDEX.bottom}` : `${Z_INDEX.level1}`)};
`;

export const ControlText = styled.div`
    display: inline;
    ${FONTS.compact.regular.size17};

    ${mediaQuery(
        { maxWidth: MAX_767 },
        css`
            ${FONTS.compact.regular.size14};
        `,
    )};

    ${mediaQuery(
        { maxWidth: MOBILE_MINIMAL_LOCAL },
        css`
            white-space: pre;
        `,
    )};
`;

export const ControlTextInteractive = styled.label`
    color: ${COLORS.textPrimaryLink};
    cursor: pointer;
`;

export const HiddenInput = styled.input`
    display: none;
`;

export const RulesText = styled.div<{ isError: boolean }>`
    height: fit-content;
    ${FONTS.compact.regular.size12};
    color: ${COLORS.textNegative};
    white-space: break-spaces;
    color: ${(p) => (p.isError ? `${COLORS.textNegative}` : `${COLORS.textSecondary}`)};
`;

export const TitleText = styled.div`
    color: ${COLORS.textSecondary};
    ${FONTS.compact.regular.size14};
    gap: ${rem(4)};
`;

export const ErrorContainer = styled.div<{ notificationPosition: { right: number; bottom: number } }>`
    display: flex;
    width: 100%;
    position: absolute;
    right: ${(p) => `${p.notificationPosition.right}px`};
    bottom: ${(p) => `${p.notificationPosition.bottom}px`};
    gap: ${rem(12)};
    align-items: flex-end;
    flex-direction: column;
    pointer-events: none;
`;

export const RequiredStar = styled.span`
    color: ${COLORS.controlsPrimaryActive};
    align-self: flex-start;
`;
